// useDataTableKeyboardNavigation.ts
import { computed, type ComputedRef, type Ref } from 'vue';

import type {
  ColumnConfig,
  SelectionIntent,
  TabRequestPayload,
} from '@/components/datatable/dataTableTypes';

type GetRowId = (row: any) => string | number | null;

type KeyboardNavDeps = {
  /** Current page slice shown in the DataTable. Navigation is scoped to this list. */
  paginatedData: Ref<any[]>;

  /** Column configuration (used to derive which columns are editable). */
  visibleColumns: ComputedRef<ColumnConfig[]>;

  /** Stable row identity function. Must return unique id per row. */
  getRowId: GetRowId;

  /**
   * Active edit-cell state owned by the DataTable component.
   * Navigation updates this to move the "cursor" (focused cell).
   */
  activeEditCell: Ref<{ rowId: string | number; field: string } | null>;

  // Selection ops (from useDataTableSelection)
  /** Ensures the target row is selected (idempotent). */
  ensureRowSelected: (row: any) => void;

  /** Replaces selection with the target row. */
  replaceSelectionWithRow: (row: any) => void;

  /** Adds the target row to the selection (multi-select). */
  addRowToSelection: (row: any) => void;

  /** Selects range from anchor to the target row (current page). */
  selectRangeToRow: (row: any) => void;

  /**
   * Optional abstraction: apply a selection intent instead of calling the concrete ops.
   * Currently not used in this implementation, but allows future simplification.
   */
  applySelectionIntent?: (row: any, intent: SelectionIntent) => void;
};

/**
 * Keyboard navigation controller for DataTable edit mode.
 *
 * Responsibilities:
 * - Maintain and update the "active cell" cursor (`activeEditCell`)
 * - Implement directional navigation:
 *   - Vertical (up/down): stays within the same column, changes row
 *   - Horizontal (forward/backward): moves across editable cells, can change row and column
 * - Apply selection policy while moving vertically:
 *   - focusOnly: move focus without changing selection (e.g. Ctrl+Arrow)
 *   - range: extend selection range to target (e.g. Shift+Arrow)
 *   - additive: add to selection (e.g. Alt+Arrow)
 *   - default: replace selection with target row (plain Arrow)
 *
 * Scope/constraints:
 * - Navigation is explicitly scoped to `paginatedData` (current page).
 * - "Editable cells" are derived from `visibleColumns` where `col.editable === true`.
 * - If horizontal navigation moves beyond the last/first editable cell, editing ends
 *   by resetting `activeEditCell` to `null`.
 */
export function useDataTableKeyboardNavigation(deps: KeyboardNavDeps) {
  const {
    paginatedData,
    visibleColumns,
    getRowId,
    activeEditCell,
    ensureRowSelected,
    replaceSelectionWithRow,
    addRowToSelection,
    selectRangeToRow,
  } = deps;

  /**
   * Subset of visible columns that are editable.
   * Used to compute the "tab order" of cells in the current page.
   */
  const editableColumns = computed(() => (visibleColumns.value ?? []).filter((c) => !!c?.editable));

  /**
   * Builds a linear list of all editable cell coordinates for the current page.
   *
   * Order:
   * - Iterate rows in `paginatedData` order
   * - For each row, iterate editable columns in `editableColumns` order
   *
   * This yields a stable “tab sequence” for horizontal navigation.
   */
  function buildEditableCellsForPage(): { rowId: string | number; field: string }[] {
    const cells: { rowId: string | number; field: string }[] = [];
    const rows = paginatedData.value ?? [];
    const cols = editableColumns.value ?? [];

    if (!rows.length || !cols.length) return cells;

    for (const row of rows) {
      const id = getRowId(row);
      if (id == null) continue;

      for (const col of cols) {
        cells.push({ rowId: id, field: col.field });
      }
    }

    return cells;
  }

  /**
   * Updates the active edit cell to the given coordinates.
   * This is the single place where we mutate `activeEditCell` for navigation.
   */
  function setActiveCell(rowId: string | number, field: string) {
    activeEditCell.value = { rowId, field };
  }

  /**
   * Handles vertical navigation (up/down).
   *
   * Behavior:
   * - Moves to the previous/next row (within current page), keeping the same column (`field`)
   * - Always updates focus (active cell)
   * - Optionally updates selection based on payload flags:
   *   - focusOnly: no selection change
   *   - range: range-select to target
   *   - additive: add target row
   *   - default: replace selection with target row
   *
   * Notes:
   * - Skips rows without a valid id.
   */
  function handleVerticalMove(payload: TabRequestPayload) {
    const { rowId, field, direction, additive = false, range = false, focusOnly = false } = payload;

    const rows = paginatedData.value ?? [];
    const currentIndex = rows.findIndex((r) => String(getRowId(r)) === String(rowId));
    if (currentIndex === -1) return;

    const step = direction === 'down' ? 1 : -1;

    let targetRow: any | null = null;
    for (let i = currentIndex + step; i >= 0 && i < rows.length; i += step) {
      const candidate = rows[i];
      if (getRowId(candidate) != null) {
        targetRow = candidate;
        break;
      }
    }
    if (!targetRow) return;

    const targetId = getRowId(targetRow);
    if (targetId == null) return;

    // 1) Always move focus
    setActiveCell(targetId, field);

    // 2) Selection policy
    if (focusOnly) return; // e.g. Ctrl+Arrow: move cursor only
    if (range) return selectRangeToRow(targetRow); // e.g. Shift+Arrow: extend range
    if (additive) return addRowToSelection(targetRow); // e.g. Alt+Arrow: add to selection

    // Default: replace selection (plain Arrow)
    return replaceSelectionWithRow(targetRow);
  }

  /**
   * Handles horizontal navigation (forward/backward).
   *
   * Behavior:
   * - Computes a linear “tab order” over editable cells on the current page
   * - Moves to the adjacent cell in that order
   * - If the next cell is out of range, editing ends (active cell reset to null)
   * - Ensures the target row is selected (keeps existing UX behavior)
   *
   * Notes:
   * - This implementation does not currently support focusOnly/additive/range horizontally.
   *   If needed, we can extend TabRequestPayload semantics similarly to vertical movement.
   */
  function handleHorizontalMove(payload: TabRequestPayload) {
    const { rowId, field, direction } = payload;

    const editableCells = buildEditableCellsForPage();
    if (!editableCells.length) return;

    const currentIndex = editableCells.findIndex(
      (c) => String(c.rowId) === String(rowId) && c.field === field,
    );
    if (currentIndex === -1) return;

    const nextIndex = direction === 'forward' ? currentIndex + 1 : currentIndex - 1;

    // Leaving the editable grid ends edit navigation
    if (nextIndex < 0 || nextIndex >= editableCells.length) {
      activeEditCell.value = null;
      return;
    }

    const nextCell = editableCells[nextIndex];
    if (!nextCell) {
      activeEditCell.value = null;
      return;
    }

    setActiveCell(nextCell.rowId, nextCell.field);

    // Keep selection consistent with the new focus row
    const targetRow = (paginatedData.value ?? []).find(
      (r) => String(getRowId(r)) === String(nextCell.rowId),
    );
    if (targetRow) ensureRowSelected(targetRow);
  }

  /**
   * Unified entry point for keyboard navigation requests.
   *
   * The DataTableCell emits a normalized `TabRequestPayload`:
   * - direction: 'up' | 'down' | 'forward' | 'backward'
   * - rowId/field: current cell coordinates
   * - optional modifiers: additive, range, focusOnly
   */
  function handleTabRequest(payload: TabRequestPayload) {
    const { direction } = payload;

    if (direction === 'up' || direction === 'down') {
      handleVerticalMove(payload);
      return;
    }

    // direction: forward | backward
    handleHorizontalMove(payload);
  }

  return {
    handleTabRequest,
    setActiveCell, // exposed for convenience (optional) / external cursor control
  };
}
